import React from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

const StorySwarms = ({ stories, minArticles = 2 }) => {
  const navigate = useNavigate();

  // Filter stories that meet the minimum article requirement
  const qualifyingStories = (stories || []).filter(
    (story) =>
      story.clustering_method === "semantic" &&
      story.article_count >= minArticles &&
      story.semantic_confidence >= 0.5
  );

  // Preserve the order from parent component (which may include regional prioritization)
  // Only sort by article count/confidence if no priorityScore is set
  const sortedStories = qualifyingStories.sort((a, b) => {
    // If both stories have priorityScore, use that for ordering (regional filtering)
    if (a.priorityScore !== undefined && b.priorityScore !== undefined) {
      return b.priorityScore - a.priorityScore;
    }
    // Otherwise, fall back to article count and confidence
    if (b.article_count !== a.article_count) {
      return b.article_count - a.article_count;
    }
    return (b.semantic_confidence || 0) - (a.semantic_confidence || 0);
  }); // Show all qualifying stories

  const formatTimeAgo = (dateString) => {
    if (!dateString) return "Unknown time";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    } else {
      return "Recently";
    }
  };

  // Calculate trending score based on article count, recency, and confidence
  const calculateTrendingScore = (story) => {
    const articleCount = story.article_count || 0;
    const confidence = story.semantic_confidence || 0;
    const hoursAgo = story.updated_at
      ? Math.floor((new Date() - new Date(story.updated_at)) / (1000 * 60 * 60))
      : 24;

    // More articles + higher confidence + more recent = higher trending score
    const recencyBonus = Math.max(0, 24 - hoursAgo) / 24; // Bonus for recency (0-1)
    const trendingScore =
      articleCount * 10 + confidence * 50 + recencyBonus * 20;

    return Math.min(100, Math.round(trendingScore));
  };

  const getTrendingIcon = (score) => {
    if (score >= 80) return "🔥"; // Hot
    if (score >= 60) return "📈"; // Trending up
    if (score >= 40) return "📊"; // Moderate activity
    return "📉"; // Low activity
  };

  const renderBiasDistribution = (biasDistribution) => {
    const total =
      biasDistribution.left + biasDistribution.center + biasDistribution.right;
    if (total === 0) return null;

    const leftPercent = (biasDistribution.left / total) * 100;
    const centerPercent = (biasDistribution.center / total) * 100;
    const rightPercent = (biasDistribution.right / total) * 100;

    return (
      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
        <div className="h-full flex">
          {leftPercent > 0 && (
            <div
              className="bg-blue-500 transition-all duration-300"
              style={{ width: `${leftPercent}%` }}
            />
          )}
          {centerPercent > 0 && (
            <div
              className="bg-gray-400 transition-all duration-300"
              style={{ width: `${centerPercent}%` }}
            />
          )}
          {rightPercent > 0 && (
            <div
              className="bg-red-500 transition-all duration-300"
              style={{ width: `${rightPercent}%` }}
            />
          )}
        </div>
      </div>
    );
  };

  const handleStoryClick = (story) => {
    navigate(`/story/${story.id}`);
  };

  if (sortedStories.length === 0) {
    return null; // Don't render if no qualifying stories
  }

  return (
    <div
      className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl shadow-lg border border-indigo-200 mb-8"
      data-testid="story-swarms-container"
    >
      <div className="p-6 border-b border-indigo-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h2 className="text-2xl font-bold text-gray-900">
              🌊 Story Swarms
            </h2>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
              🧠 All AI-Connected Stories
            </span>
          </div>
          <div className="text-sm text-gray-600">
            <p>Stories with {minArticles}+ articles connected by AI</p>
            <p className="text-xs mt-1 text-gray-500">
              Showing all {sortedStories.length} qualifying stories
            </p>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {sortedStories.map((story) => {
            const trendingScore = calculateTrendingScore(story);
            const trendingIcon = getTrendingIcon(trendingScore);

            return (
              <div
                key={story.id}
                className="bg-white rounded-lg border border-indigo-200 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer group"
                onClick={() => handleStoryClick(story)}
              >
                <div className="p-5">
                  {/* Header - Trending and Topic Tags */}
                  <div className="flex items-center flex-wrap gap-2 mb-4">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                      {trendingIcon} {trendingScore}% Trending
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {story.topic || "General"}
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                      {story.article_count}{" "}
                      {story.article_count === 1 ? "article" : "articles"}
                    </span>
                  </div>

                  {/* Title - Now has full width */}
                  <h3 className="font-semibold text-xl text-gray-900 mb-4 group-hover:text-indigo-600 transition-colors leading-tight">
                    {story.title}
                  </h3>

                  {/* AI Summary */}
                  <div className="mb-4">
                    {story.ai_summary ? (
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {story.ai_summary}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500 italic leading-relaxed">
                        AI summary loading...
                      </p>
                    )}
                  </div>

                  {/* Bias Distribution */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-700">
                        Bias Distribution
                      </span>
                      <div className="flex items-center space-x-3 text-xs text-gray-600">
                        <span className="flex items-center">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mr-1"></div>
                          Left ({story.bias_distribution?.left || 0})
                        </span>
                        <span className="flex items-center">
                          <div className="w-2 h-2 bg-gray-400 rounded-full mr-1"></div>
                          Center ({story.bias_distribution?.center || 0})
                        </span>
                        <span className="flex items-center">
                          <div className="w-2 h-2 bg-red-500 rounded-full mr-1"></div>
                          Right ({story.bias_distribution?.right || 0})
                        </span>
                      </div>
                    </div>
                    {renderBiasDistribution(
                      story.bias_distribution || {
                        left: 0,
                        center: 0,
                        right: 0,
                      }
                    )}
                  </div>

                  {/* Regional Distribution */}
                  {story.region_distribution && (
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-700">
                          Regional Sources
                        </span>
                        <div className="flex items-center space-x-2 text-xs text-gray-600">
                          {story.region_distribution.uk > 0 && (
                            <span className="flex items-center">
                              🇬🇧 {story.region_distribution.uk}
                            </span>
                          )}
                          {story.region_distribution.us > 0 && (
                            <span className="flex items-center">
                              🇺🇸 {story.region_distribution.us}
                            </span>
                          )}
                          {story.region_distribution.international > 0 && (
                            <span className="flex items-center">
                              🌍 {story.region_distribution.international}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Footer - Last Updated and Action */}
                  <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                    <span className="text-xs text-gray-500">
                      Updated {formatTimeAgo(story.updated_at)}
                    </span>
                    <div className="flex items-center text-xs text-indigo-600 font-medium group-hover:text-indigo-700">
                      View Analysis →
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

StorySwarms.propTypes = {
  stories: PropTypes.array.isRequired,
  minArticles: PropTypes.number,
};

export default StorySwarms;
